import React, {useCallback, useEffect, useEffectEvent, useMemo, useState} from 'react'
import {RepoIcon, RepoLockedIcon, ShieldLockIcon, TriangleDownIcon} from '@primer/octicons-react'
import type {AnchorSide} from '@primer/behaviors'
import {Button, IconButton, SelectPanel, Truncate, type ButtonProps, type OverlayProps} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'
import {ItemPicker} from '@github-ui/item-picker-base/ItemPicker'

import styles from './ReposSelector.module.css'

// A simple repository that only requires a `name` field but can contain additional
// fields to enrich rendering
interface NamedRepository {
  name: string
  enabled?: boolean
  private?: boolean
}

/**
 * Accessible label announced on the header restriction shield. Names the owner
 * whose private repositories the picker is scoped to.
 */
export function restrictionShieldLabel(owner: string): string {
  return `Private repository selection is restricted to ${owner}.`
}

/**
 * Shield shown beside a repo picker's title when an owner scope is active.
 */
export function RestrictionShield({owner}: {owner: string}) {
  const label = restrictionShieldLabel(owner)

  return (
    <span role="img" aria-label={label} data-testid="repo-restriction-shield" className={styles.restrictionShield}>
      <ShieldLockIcon aria-hidden="true" size={16} />
    </span>
  )
}

// Internal repo used to represent "All repositories"
interface AllRepo extends NamedRepository {
  _all: true
}

const ALL_REPOSITORIES: AllRepo = {name: '$all$', _all: true}

// Internal repo used to represent "No repository"
interface NoRepo extends NamedRepository {
  _noRepo: true
}

interface AllRepositoriesRepo extends NamedRepository {
  _allRepositories: true
}

const NO_REPOSITORY: NoRepo = {name: '$none$', _noRepo: true}
const ALL_REPOSITORIES_ONLY: AllRepositoriesRepo = {name: '$repositories$', _allRepositories: true}

/** Type guard to check if a selection from ReposSelector is the "No repository" sentinel. */
export function isNoRepoSelection(repo: unknown): boolean {
  return typeof repo === 'object' && repo !== null && '_noRepo' in repo
}

/** Type guard to check if a selection from ReposSelector is the "All repositories" sentinel. */
export function isAllRepositoriesSelection(repo: unknown): boolean {
  return typeof repo === 'object' && repo !== null && '_allRepositories' in repo
}

interface SharedProps {
  selectAllOption: boolean
  /** Controls whether "All repositories" is separated from or included with repository results. */
  selectAllOptionPlacement?: 'grouped' | 'inline'
  /** Overrides the label for the select-all option. */
  selectAllOptionText?: string
  /** Optional leading visual for the select-all option. */
  selectAllOptionLeadingVisual?: React.ComponentType
  /** When true, marks the select-all option as the controlled single-selection value. */
  selectAllOptionSelected?: boolean
  /** Optional external anchor used to position and restore focus for controlled pickers. */
  selectPanelRef?: React.RefObject<HTMLButtonElement | null>
  /** When true, adds a "No repository" option to the picker for filtering repo-less items */
  noRepoOption?: boolean
  /** When true, marks the "No repository" option as the controlled single-selection value. */
  noRepoOptionSelected?: boolean
  /** When true, adds an "All repositories" option distinct from the select-all option. */
  allRepositoriesOption?: boolean
  /** When true, marks the "All repositories" option as the controlled single-selection value. */
  allRepositoriesOptionSelected?: boolean
  additionalButtonProps?: Partial<ButtonProps>
  buttonText?: string
  /** When true, applies the selected styling (blue background) when repos are selected (defaults to true) */
  highlightSelected?: boolean
  width?: OverlayProps['width']
  // Optional max width in pixels for button label truncation
  truncateMaxWidth?: number
  // When true, disable truncation of repository names in the overlay based on the width of the overlay
  disableItemTruncation?: boolean
  /** Height passed to the underlying overlay (defaults to ItemPicker default if omitted). */
  height?: OverlayProps['height']
  /** When set, sizes the repository list to six visible selectable rows before scrolling. */
  visibleRowCount?: 6
  /** When true, enable fullscreen behavior on narrow viewports (maps to ItemPicker responsiveOnNarrow). */
  fullscreenOnNarrow?: boolean
  loadingElement?: React.ReactNode
  /** When true, enable live selection mode where changes are applied immediately. Only applicable for multiple selection variant. */
  liveSelection?: boolean
  /** Optional callback invoked when the overlay is opened */
  onOpen?: () => void
  /** Optional callback invoked when the overlay is closed */
  onClose?: () => void
  /** Responsive styling variant: 'full' always shows text, 'icon-on-mobile' hides text on small viewports */
  responsiveStyle?: 'full' | 'icon-on-mobile'
  /** When true, always show only the icon button regardless of viewport size */
  iconOnly?: boolean
  /**
   * Render a fully custom anchor instead of the default button / icon button (e.g. an
   * `ActionList.Item` for nesting inside an ActionBar overflow menu). Receives the anchor props
   * to spread onto the rendered element, plus the resolved display text and accessible label.
   */
  renderAnchor?: (args: {
    anchorProps: React.HTMLAttributes<HTMLElement>
    buttonText: string
    ariaLabel: string
  }) => React.ReactElement
  /** The preferred side for the overlay to open relative to the anchor. */
  anchorSide?: AnchorSide
  /** The direction for the tooltip to appear when using icon-only mode */
  tooltipDirection?: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
  /** The portal container name for the overlay. Used for rendering the overlay in a specific portal container. */
  portalContainerName?: string
  /** The position style for the overlay (e.g., 'relative', 'fixed'). */
  overlayPosition?: 'relative' | 'fixed'
  /** When set, controls whether the picker overlay is open (controlled mode). */
  triggerOpen?: boolean
  /** Optional secondary action to display in the footer of the picker overlay. */
  secondaryAction?:
    | React.ReactElement<typeof SelectPanel.SecondaryActionButton>
    | React.ReactElement<typeof SelectPanel.SecondaryActionLink>
  /**
   * Optional subtitle rendered under the panel's "Select a repository" title.
   * Useful for surfacing scope hints such as which owner's repos are shown.
   */
  subtitle?: string | React.ReactElement
  /**
   * When set, an owner scope is active: the picker shows a {@link RestrictionShield}
   * beside its title naming this owner, and private repository selection is limited
   * to it. Consumers pass the composer's active scope owner (github-ui#31409).
   */
  restrictionOwner?: string
}

export interface SingleSelectProps<T> extends SharedProps {
  currentSelection: T | undefined
  repositoryLoader: (q: string, defaultOwner?: string) => Promise<T[]>
  onSelect: (repository: T | undefined) => void
  selectionVariant: 'single'
  /** Optional organization login to scope repository search */
  defaultOwner?: string
  /** Optional callback invoked when repositories are loaded */
  onLoad?: (repositories: T[]) => void
}

export interface MultiSelectProps<T> extends SharedProps {
  currentSelection: T[] | undefined
  repositoryLoader: (q: string, defaultOwner?: string) => Promise<T[]>
  onSelect: (repository: T[] | undefined) => void
  selectionVariant: 'multiple'
  selectAllOption: false
  additionalButtonProps?: Partial<ButtonProps>
  /** Optional organization login to scope repository search */
  defaultOwner?: string
  /** Optional callback invoked when repositories are loaded */
  onLoad?: (repositories: T[]) => void
}

export function ReposSelector<T extends NamedRepository>({
  currentSelection,
  repositoryLoader,
  onSelect,
  selectionVariant,
  selectAllOption,
  selectAllOptionPlacement = 'grouped',
  selectAllOptionText = 'All repositories',
  selectAllOptionLeadingVisual,
  selectAllOptionSelected = false,
  noRepoOption = false,
  noRepoOptionSelected = false,
  allRepositoriesOption = false,
  allRepositoriesOptionSelected = false,
  additionalButtonProps,
  buttonText: _buttonText = '',
  width,
  truncateMaxWidth,
  disableItemTruncation = false,
  height,
  visibleRowCount,
  fullscreenOnNarrow,
  defaultOwner,
  onLoad,
  loadingElement,
  liveSelection = false,
  onOpen,
  onClose,
  triggerOpen,
  selectPanelRef,
  responsiveStyle = 'full',
  iconOnly = false,
  renderAnchor: renderCustomAnchor,
  anchorSide,
  tooltipDirection,
  portalContainerName,
  overlayPosition,
  secondaryAction,
  subtitle,
  highlightSelected = true,
  restrictionOwner,
}: SingleSelectProps<T> | MultiSelectProps<T>) {
  const [filterText, setFilterText] = useState('')
  const [filteredRepos, setFilteredRepos] = useState<T[]>([])
  const [isLoadingRepos, setIsLoadingRepos] = useState<boolean>(true)
  const [initialLoadComplete, setInitialLoadComplete] = useState(false)

  const loadRepos = useEffectEvent(async (filter: string) => {
    try {
      const response = await repositoryLoader(filter, defaultOwner)
      setFilteredRepos(response)
      onLoad?.(response)
    } catch {
      // suppress repo loading errors
    }
    setIsLoadingRepos(false)
    setInitialLoadComplete(true)
  })

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsLoadingRepos(true)
    const timeoutId = setTimeout(() => {
      loadRepos(filterText)
    }, 200)
    return () => clearTimeout(timeoutId)
  }, [filterText, repositoryLoader, defaultOwner])

  const selectRepos = useCallback(
    (repos: Array<T | AllRepo | NoRepo | AllRepositoriesRepo>) => {
      if (selectionVariant === 'single') {
        if (!repos[0]) {
          if (!selectAllOption || currentSelection === undefined) onSelect(undefined)
          return
        }
        if ((repos[0] as AllRepo)._all) {
          onSelect(undefined)
        } else {
          onSelect(repos[0] as T)
        }
      } else {
        onSelect(repos as T[])
      }
    },
    [currentSelection, onSelect, selectAllOption, selectionVariant],
  )

  const selected = useMemo(() => {
    if (selectionVariant === 'multiple') return currentSelection || []
    if (selectAllOption && selectAllOptionSelected) return [ALL_REPOSITORIES]
    if (allRepositoriesOption && allRepositoriesOptionSelected) return [ALL_REPOSITORIES_ONLY]
    if (noRepoOption && noRepoOptionSelected) return [NO_REPOSITORY]
    return currentSelection ? [currentSelection] : []
  }, [
    allRepositoriesOption,
    allRepositoriesOptionSelected,
    currentSelection,
    noRepoOption,
    noRepoOptionSelected,
    selectAllOption,
    selectAllOptionSelected,
    selectionVariant,
  ])
  const hasSelection =
    currentSelection !== undefined ||
    (selectionVariant === 'single' &&
      ((selectAllOption && selectAllOptionSelected) ||
        (allRepositoriesOption && allRepositoriesOptionSelected) ||
        (noRepoOption && noRepoOptionSelected)))

  const convertToItemProps = useCallback(
    (repo: T | AllRepo | NoRepo | AllRepositoriesRepo) => {
      // Character limits based on overlay width:
      // small (256px) -> 24 chars, medium (320px) -> 35 chars, large (480px) -> 60 chars
      const truncationCharLimit = width === 'medium' ? 35 : width === 'large' ? 60 : 24
      let truncatedRepoName = undefined

      if (!disableItemTruncation && repo.name.length > truncationCharLimit) {
        truncatedRepoName = `${repo.name.substring(0, truncationCharLimit)}...`
      }

      if ((repo as AllRepo)._all) {
        return {
          id: repo.name,
          text: selectAllOptionText,
          source: repo,
          groupId: selectAllOptionPlacement === 'inline' ? 'select' : 'all',
          leadingVisual: selectAllOptionLeadingVisual,
        }
      }
      if ((repo as NoRepo)._noRepo) {
        return {
          id: repo.name,
          text: 'No repository',
          source: repo,
          groupId: 'all',
        }
      }
      if ((repo as AllRepositoriesRepo)._allRepositories) {
        return {
          id: repo.name,
          text: 'All repositories',
          source: repo,
          groupId: 'all',
        }
      }
      return {
        id: repo.name,
        text: truncatedRepoName || repo.name,
        source: repo,
        groupId: 'select',
        disabled: repo.enabled === false,
        leadingVisual: () => {
          if (repo.private) {
            return <RepoLockedIcon size={16} />
          } else {
            return <RepoIcon size={16} />
          }
        },
      }
    },
    [width, disableItemTruncation, selectAllOptionLeadingVisual, selectAllOptionPlacement, selectAllOptionText],
  )

  const getItemKey = useCallback((repo: T | AllRepo | NoRepo | AllRepositoriesRepo) => repo.name, [])

  const items = useMemo(() => {
    const memoItems: Array<T | AllRepo | NoRepo | AllRepositoriesRepo> = []
    if (selectAllOption) {
      memoItems.push(ALL_REPOSITORIES)
    }
    if (allRepositoriesOption) {
      memoItems.push(ALL_REPOSITORIES_ONLY)
    }
    if (noRepoOption) {
      memoItems.push(NO_REPOSITORY)
    }
    memoItems.push(...filteredRepos)
    return memoItems
  }, [allRepositoriesOption, filteredRepos, noRepoOption, selectAllOption])

  let buttonText = _buttonText
  let buttonIcon = RepoIcon
  if (!buttonText) {
    if (selectionVariant === 'multiple') {
      buttonText =
        selected.length > 0 ? `${selected.length} repositor${selected.length > 1 ? 'ies' : 'y'}` : 'Select repositories'
    } else {
      if (selected.length > 0 && selected[0]) {
        if ((selected[0] as AllRepo)._all) {
          buttonText = selectAllOptionText
        } else if ((selected[0] as AllRepositoriesRepo)._allRepositories) {
          buttonText = 'All repositories'
        } else if ((selected[0] as NoRepo)._noRepo) {
          buttonText = 'No repository'
        } else {
          buttonText = selected[0].name
          buttonIcon = selected[0].private ? RepoLockedIcon : RepoIcon
        }
      } else if (selectAllOption) {
        buttonText = selectAllOptionText
      } else {
        buttonText = 'Select a repository'
      }
    }
  }

  // Primer's narrow viewport range
  const isNarrowViewport = useMediaQuery('(max-width: calc(48rem - 0.02px))')
  const isNarrow = responsiveStyle === 'icon-on-mobile' && isNarrowViewport
  // Show icon only when iconOnly prop is true or on narrow screens with icon-on-mobile style
  const showIconOnly = iconOnly || isNarrow

  const pickerTitle = restrictionOwner ? (
    <span className={styles.titleWithShield}>
      Select a repository
      <RestrictionShield owner={restrictionOwner} />
    </span>
  ) : (
    'Select a repository'
  )

  return loadingElement && isLoadingRepos && !hasSelection && !initialLoadComplete ? (
    loadingElement
  ) : (
    <ItemPicker
      renderAnchor={({'aria-labelledby': ariaLabelledBy, ...anchorProps}) => {
        const {'aria-label': _ariaLabel, ...iconAnchorProps} = anchorProps
        const iconButtonAriaLabel = additionalButtonProps?.['aria-label'] ?? _ariaLabel ?? buttonText
        if (renderCustomAnchor) {
          return renderCustomAnchor({anchorProps: iconAnchorProps, buttonText, ariaLabel: iconButtonAriaLabel})
        }
        return showIconOnly ? (
          <IconButton
            variant="invisible"
            {...(ariaLabelledBy ? {'aria-labelledby': ariaLabelledBy} : {'aria-label': iconButtonAriaLabel})}
            icon={(additionalButtonProps?.leadingVisual as React.ElementType) || buttonIcon}
            {...iconAnchorProps}
            className={`${styles.invisibleWithBorder}${highlightSelected && selected.length ? ` ${styles.selected}` : ''}`}
            tooltipDirection={tooltipDirection}
          />
        ) : (
          <Button
            variant={responsiveStyle === 'icon-on-mobile' ? 'invisible' : undefined}
            leadingVisual={buttonIcon}
            trailingAction={TriangleDownIcon}
            aria-labelledby={ariaLabelledBy}
            {...anchorProps}
            {...additionalButtonProps}
            className={`${additionalButtonProps?.className ?? ''}${
              responsiveStyle === 'icon-on-mobile' ? ` ${styles.invisibleWithBorder}` : ''
            }`}
          >
            <Truncate title={buttonText} maxWidth={truncateMaxWidth ?? 195}>
              {buttonText}
            </Truncate>
          </Button>
        )
      }}
      placeholderText="Search"
      items={items}
      loading={isLoadingRepos}
      groups={
        (selectAllOption && selectAllOptionPlacement === 'grouped') || allRepositoriesOption || noRepoOption
          ? [{groupId: 'all'}, {groupId: 'select'}]
          : []
      }
      selectionVariant={selectionVariant}
      initialSelectedItems={selected}
      onSelectionChange={selectRepos}
      onLiveSelectionChange={selectionVariant === 'multiple' && liveSelection ? selectRepos : undefined}
      filterItems={setFilterText}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      title={pickerTitle}
      subtitle={subtitle}
      width={width}
      height={visibleRowCount ? 'auto' : height}
      maxVisibleItems={visibleRowCount}
      className={visibleRowCount ? styles.sixVisibleItems : undefined}
      responsiveOnNarrow={fullscreenOnNarrow}
      onOpen={onOpen}
      onClose={onClose}
      triggerOpen={triggerOpen}
      selectPanelRef={selectPanelRef}
      anchorSide={anchorSide}
      portalContainerName={portalContainerName}
      overlayPosition={overlayPosition}
      secondaryAction={secondaryAction}
    />
  )
}

export function simpleRepoLoader<T extends NamedRepository>(repos: T[]): (q: string, org?: string) => Promise<T[]> {
  return async (q: string, _org?: string) => {
    const trimmedFilterText = q.trim().toLowerCase()

    if (!trimmedFilterText) {
      return repos
    }

    return repos.filter(
      repo =>
        repo.name.toLowerCase().includes(trimmedFilterText) || repo.name.toLowerCase().includes(trimmedFilterText),
    )
  }
}
